import React, { useState, useEffect } from "react";
import {
  useDeleteSpecialMutation,
  useGetSpecialsQuery,
  useUpdateSpecialMutation,
} from "state/api";
import { Edit, Trash2, Save, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getColumns } from "./columns";
import SpecialsAppBar from "../components/AppBar";
import TailwindTable from "../components/TailwindTable";
import SpecialsStatsCard, { SpecialsBreakdown } from "../components/SpecialsStatsCard";

export const Specials = () => {
  const { data: specials = [], isLoading: isLoadingSpecials } = useGetSpecialsQuery();
  const [updateSpecial] = useUpdateSpecialMutation();
  const [deleteSpecial] = useDeleteSpecialMutation();
  const navigate = useNavigate();

  // Estados
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSpecials, setFilteredSpecials] = useState([]);
  const [showDetailedStats, setShowDetailedStats] = useState(false);

  
  // Filtrar especiais com base no termo de busca
  useEffect(() => {
    if (specials && specials.length > 0) {
      if (!searchTerm) {
        setFilteredSpecials(specials);
      } else {
        const lowercaseSearch = searchTerm.toLowerCase();
        const filtered = specials.filter(special => 
          special.name?.toLowerCase().includes(lowercaseSearch) ||
          special.description?.toLowerCase().includes(lowercaseSearch) ||
          special.slug?.toLowerCase().includes(lowercaseSearch)
        );
        setFilteredSpecials(filtered);
      }
    }
  }, [specials, searchTerm]);

  // Função para começar a edição - redirecionar para página de edit
  const handleEdit = (special) => {
    navigate(`/special/edit/${special.id}`);
  };

  // Função para salvar a edição
  const handleSave = async () => {
    try {
      await updateSpecial({
        id: editingId,
        ...editValues
      }).unwrap();
      setEditingId(null);
    } catch (error) {
      console.error("Erro ao atualizar especial:", error);
    }
  };

  // Função para cancelar a edição
  const handleCancel = () => {
    setEditingId(null);
  };

  // Função para excluir um especial
  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir este especial?")) {
      try {
        await deleteSpecial(id).unwrap();
      } catch (error) {
        console.error("Erro ao excluir especial:", error);
      }
    }
  };

  // Função para atualizar campos específicos
  const handleUpdate = async (id, field, value) => {
    try {
      await updateSpecial({ id, [field]: value }).unwrap();
    } catch (error) {
      console.error('Erro ao atualizar:', error);
    }
  };

  // Função para lidar com mudanças nos inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Redirecionamento para página de criação
  const handleCreate = () => {
    navigate('/special/add');
  };

  // Componente para renderizar as ações de cada linha
  const renderRowActions = (row) => {
    return (
      <div className="flex space-x-2">
        <button
          onClick={() => handleEdit(row)}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 h-8 w-8 p-0"
          title="Editar"
        >
          <Edit className="h-4 w-4" />
        </button>
        <button
          onClick={() => handleDelete(row.id)}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-red-100 text-red-800 hover:bg-red-200 h-8 w-8 p-0"
          title="Excluir"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    );
  };

  // Busca com filtragem explícita
  const handleSearch = () => {
    if (specials && specials.length > 0) {
      const lowercaseSearch = searchTerm.toLowerCase();
      const filtered = specials.filter(special => 
        special.name?.toLowerCase().includes(lowercaseSearch) ||
        special.description?.toLowerCase().includes(lowercaseSearch) ||
        special.slug?.toLowerCase().includes(lowercaseSearch)
      );
      setFilteredSpecials(filtered);
    }
  };

  // Função para exportar dados
  const handleExport = () => {
    console.log('Exportar especiais');
  };

  // Obter as definições de colunas
  const columns = getColumns({ 
    onUpdate: handleUpdate, 
    onDelete: handleDelete 
  });
  
  return (
    <div className="flex flex-col space-y-4 p-4 min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50">
      {/* Cabeçalho com cards de estatísticas */}
      <div className="rounded-lg bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-600 p-6 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">
              Gerenciamento de Especiais
            </h1>
            <p className="text-purple-100">
              Visualize, edite e gerencie seus especiais
            </p>
          </div>
          <button
            onClick={() => setShowDetailedStats(!showDetailedStats)}
            className="px-4 py-2 bg-white/20 backdrop-blur rounded-md text-white hover:bg-white/30 transition-colors text-sm"
          >
            {showDetailedStats ? 'Ocultar Detalhes' : 'Ver Detalhes'}
          </button>
        </div>
        
        {/* Cards de Estatísticas */}
        <SpecialsStatsCard data={specials} />
        
        {/* Breakdown detalhado (opcional) */}
        {showDetailedStats && (
          <div className="mt-4">
            <SpecialsBreakdown data={specials} />
          </div>
        )}
      </div>

      {/* AppBar com ações */}
      <SpecialsAppBar 
        handleCreate={handleCreate}
        handleSearch={handleSearch}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExport={handleExport}
      />

      {/* Tabela de dados */}
      <TailwindTable
        columns={columns}
        data={filteredSpecials}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={filteredSpecials.length}
        renderRowActions={renderRowActions}
        isLoading={isLoadingSpecials}
      />
    </div>
  );
};

export default React.memo(Specials);